import { useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useAudit } from "./AuditContext";
import { tapFeedback } from "./haptics";
import { PresenterFloorShadow } from "./PresenterFloorShadow";
import { useIsMobileAudit } from "./useIsMobileAudit";

/**
 * Rehearsal Room · Room 03 — VOICE
 * =================================
 * Vocal-score exercise. The user marks:
 *   • EMPHASIS  → tap a word to give it emphasis (max 3)
 *   • PAUSES    → tap the space BETWEEN words to insert "/" (max 2)
 *
 * There is NO real voice / mic capture, NO scoring, NO AI. This is an
 * intentionality exercise.
 *
 * Scene composition (per reference):
 *   – microphone visible on the LEFT (from bg_voice.png)
 *   – Presenter on the RIGHT (animated sprite)
 *   – sentence in a warm cream translucent bar across the centre-left
 *   – gentle dark scrim on the room (NOT on the presenter or sentence)
 *
 * Two visual modes:
 *   MODE A (coaching)   — Presenter large, big compact bubble
 *   MODE B (exercising) — Presenter smaller, bubble hidden
 * Framer Motion animates the transition between the two.
 */

// ---------------------------------------------------------------------------
// Content
// ---------------------------------------------------------------------------
const SENTENCE = "We don\u2019t need more information, we need a decision.";
// Tokenise into words that survive tap identification.
const WORDS = SENTENCE.split(" ").map((w, i) => ({ id: `w${i}`, text: w }));

const INTRO_STEPS = [
  "Now let\u2019s give the words a voice.",
  "Read this line silently first.",
  "Which words would you want the audience to really hear?",
  "Tap those words to give them emphasis.",
  "And where would you pause?",
  "Tap between two words to place a /.",
  "There\u2019s no single right answer.",
  "Score the line the way you would say it.",
];

const SUCCESS_STEPS = [
  "Good. That helps me understand what was behind your emphasis and pauses.",
  "Now let\u2019s add another layer of meaning.",
  "Let\u2019s look at Gesture.",
];

const REFLECTION_QUESTION = "What guided your emphasis and pauses most?";
const REFLECTION_OPTIONS = [
  { id: "meaning",           label: "Meaning",            hint: "Making the key idea clearer" },
  { id: "importantWords",    label: "Important words",    hint: "Making certain words stand out" },
  { id: "rhythm",            label: "Rhythm",             hint: "Making the sentence feel more natural" },
  { id: "audienceAttention", label: "Audience attention", hint: "Helping people stay with the message" },
  { id: "instinct",          label: "Instinct",           hint: "It just felt like the way I would say it" },
];

const MAX_EMPHASIS = 3;
const MAX_PAUSES = 2;

// Same soft mask as Room 01/02 to hide the sprite's spotlights + shadow.
const PRESENTER_MASK =
  "linear-gradient(to bottom, transparent 0%, black 5%, black 95%, transparent 100%)";

const EASE = [0.16, 1, 0.3, 1];
const MODE_TRANSITION = { duration: 0.9, ease: EASE };

// ---------------------------------------------------------------------------
// Vocal score — interactive sentence
// ---------------------------------------------------------------------------
function VocalScore({ emphasis, pauses, interactive, onToggleWord, onTogglePause }) {
  return (
    <div
      className="flex flex-wrap items-center justify-center gap-y-2"
      data-testid="voice-sentence"
      style={{ rowGap: 8 }}
    >
      {WORDS.map((w, i) => {
        const isEmph = emphasis.includes(w.id);
        return (
          <span key={w.id} className="inline-flex items-center">
            <button
              type="button"
              disabled={!interactive}
              onClick={() => interactive && onToggleWord(w.id)}
              data-testid={`voice-word-${w.id}`}
              className={[
                "font-serif transition-all duration-200 px-1.5 py-0.5",
                "text-[28px] md:text-[34px] leading-snug",
                interactive ? "cursor-pointer" : "cursor-default",
                isEmph
                  ? "text-terracotta font-semibold"
                  : "text-ink hover:bg-mustard/30",
              ].join(" ")}
              style={
                isEmph
                  ? { background: "rgba(216, 176, 96, 0.18)", borderRadius: 3 }
                  : undefined
              }
            >
              {w.text}
            </button>

            {/* Interactive gap AFTER this word (except the very last) */}
            {i < WORDS.length - 1 && (
              <button
                type="button"
                disabled={!interactive}
                onClick={() => interactive && onTogglePause(i)}
                aria-label={`Toggle pause after word ${i + 1}`}
                data-testid={`voice-gap-${i}`}
                className={[
                  "font-serif transition-colors duration-200 px-1.5",
                  "text-[28px] md:text-[34px] leading-snug",
                  interactive ? "cursor-pointer" : "cursor-default",
                ].join(" ")}
                style={{
                  color: pauses.includes(i) ? "#495235" : "transparent",
                  fontWeight: pauses.includes(i) ? 700 : 400,
                  minWidth: 22,
                  textAlign: "center",
                }}
              >
                {pauses.includes(i) ? "/" : "·"}
              </button>
            )}
          </span>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Room component
// ---------------------------------------------------------------------------
export function RehearsalRoomVoice() {
  const {
    answers,
    setRoomAnswer,
    setTagsForRoom,
    setCanProceed,
    setOverrideMessage,
    setBeforeNext,
    setReflection,
    selectedPresenter,
    overrideMessage,
    next,
  } = useAudit();
  const isMobile = useIsMobileAudit();

  const introStep     = answers.voice?.introStep     ?? 0;
  const successStep   = answers.voice?.successStep   ?? 0;
  const emphasis      = answers.voice?.emphasis      ?? [];
  const pauses        = answers.voice?.pauses        ?? [];
  const flowConfirmed = answers.voice?.flowConfirmed ?? false;
  const introDone     = introStep >= INTRO_STEPS.length;
  const hasMinChoices = emphasis.length >= 1 && pauses.length >= 1;
  const reflectionPick = answers.voice?.reflection?.selected ?? null;
  const inReflectPick  = flowConfirmed && !reflectionPick;
  const successDone   = flowConfirmed && !!reflectionPick && successStep >= SUCCESS_STEPS.length;
  const beatsPlaying  = flowConfirmed && !!reflectionPick && !successDone;

  // MODE A = coaching, MODE B = exercising.
  // A during intro, reflection and post-task beats; B while user is scoring.
  const modeCoaching  = !introDone || inReflectPick || beatsPlaying || successDone;
  const showBubble    = !introDone || inReflectPick || beatsPlaying || successDone;
  const inCatcher     = !introDone || beatsPlaying;

  // Sync bubble + continue gate.
  useEffect(() => {
    if (!introDone) {
      setOverrideMessage(INTRO_STEPS[introStep]);
      setCanProceed(false);
      return;
    }
    if (!flowConfirmed) {
      setOverrideMessage("");
      setCanProceed(hasMinChoices);
      return;
    }
    if (!reflectionPick) {
      setOverrideMessage(REFLECTION_QUESTION);
      setCanProceed(false);
      return;
    }
    if (!successDone) {
      setOverrideMessage(SUCCESS_STEPS[successStep]);
      setCanProceed(false);
      return;
    }
    // Success beats finished — the room already schedules its own
    // auto-advance to the next room 900ms after the final beat tap (see
    // `advance()` below). Leaving Continue disabled here (rather than
    // true) closes a real race: if it were enabled, a click landing in
    // that same ~900ms window would fire next() a SECOND time, which
    // then double-advances past the room after this one entirely.
    setOverrideMessage(SUCCESS_STEPS[SUCCESS_STEPS.length - 1]);
    setCanProceed(false);
  }, [introDone, introStep, flowConfirmed, hasMinChoices, reflectionPick, successStep, successDone, setCanProceed, setOverrideMessage]);

  // Intercept Continue during the confirm phase.
  useEffect(() => {
    setBeforeNext(() => {
      if (introDone && !flowConfirmed && hasMinChoices) {
        setRoomAnswer("voice", { flowConfirmed: true, successStep: 0 });
        return false;
      }
      if (flowConfirmed && !successDone) return false;
      return true;
    });
    return () => setBeforeNext(null);
  }, [introDone, flowConfirmed, hasMinChoices, successDone, setBeforeNext, setRoomAnswer]);

  // Persist tags whenever choices change.
  useEffect(() => {
    const tags = [
      ...emphasis.map((id) => `voice:emphasis:${id}`),
      ...pauses.map((p) => `voice:pause:${p}`),
    ];
    setTagsForRoom("voice", tags);
  }, [emphasis.join("|"), pauses.join("|"), setTagsForRoom]);

  // Actions
  const toggleWord = (wordId) => {
    if (flowConfirmed) return;
    tapFeedback();
    if (emphasis.includes(wordId)) {
      setRoomAnswer("voice", { emphasis: emphasis.filter((id) => id !== wordId) });
      return;
    }
    if (emphasis.length >= MAX_EMPHASIS) return;
    setRoomAnswer("voice", { emphasis: [...emphasis, wordId] });
  };

  const togglePause = (gapIndex) => {
    if (flowConfirmed) return;
    tapFeedback();
    if (pauses.includes(gapIndex)) {
      setRoomAnswer("voice", { pauses: pauses.filter((p) => p !== gapIndex) });
      return;
    }
    if (pauses.length >= MAX_PAUSES) return;
    setRoomAnswer("voice", { pauses: [...pauses, gapIndex].sort((a, b) => a - b) });
  };

  const advance = () => {
    if (!introDone) {
      setRoomAnswer("voice", { introStep: introStep + 1 });
      return;
    }
    if (beatsPlaying) {
      const nextStep = successStep + 1;
      setRoomAnswer("voice", { successStep: nextStep });
      // If the user just tapped the FINAL beat (the "walking" pose /
      // "Come with me." line), advance to the next room automatically after
      // a brief moment so the walking presenter is visible for a beat.
      if (nextStep >= SUCCESS_STEPS.length) {
        setTimeout(() => {
          setBeforeNext(null);
          next();
        }, 900);
      }
    }
  };

  const pickReflection = (optionId) => {
    if (!inReflectPick) return;
    tapFeedback();
    const tag = `voice_reflection:${optionId}`;
    setRoomAnswer("voice", { reflection: { selected: optionId, tag }, successStep: 0 });
    setReflection("voice", { question: REFLECTION_QUESTION, selected: optionId, tag });
    setTagsForRoom("voice_reflection", [tag]);
  };

  const interactive = introDone && !flowConfirmed;

  const presenter = selectedPresenter;
  // Walking pose starts on the final beat and stays through the brief
  // hand-off to Room 04 so the user actually SEES the "walking away" moment.
  const onFinalBeat = flowConfirmed && successStep >= SUCCESS_STEPS.length - 1;
  // Once the user taps PAST the last beat, we're in the ~900ms exit window
  // — the presenter drifts left and fades before Room 04 mounts.
  const isLeaving = flowConfirmed && successStep >= SUCCESS_STEPS.length;
  const pose = modeCoaching
    ? (onFinalBeat ? "walking" : "presentingAlt")
    : "listening";
  const poseFile = presenter?.poses?.[pose] || presenter?.poses?.neutral;
  const presenterSrc = presenter ? `${presenter.avatarBase}/${poseFile}` : "";
  const flipHorizontally = pose === "presentingAlt";

  const totalCatcherSteps = !introDone ? INTRO_STEPS.length : SUCCESS_STEPS.length;
  const currentCatcherStep = !introDone ? introStep + 1 : successStep + 1;

  return (
    <div
      className="absolute inset-0 overflow-hidden"
      data-testid="rehearsal-room-voice"
      style={{ background: "#141310" }}
    >
      {/* Background — rehearsal room with microphone + wall + paintings */}
      <div
        aria-hidden="true"
        className="absolute inset-0"
        style={{
          backgroundImage: "url(/audit/bg_voice.webp)",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />

      {/* Focus scrim — subtle dark overlay across the room (behind sentence + presenter) */}
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none"
        style={{
          background: "rgba(10, 8, 5, 0.32)",
          zIndex: 5,
        }}
      />

      {/* Sentence bar — warm cream translucent, sits center-left.
          Cleared once the score is confirmed (reflection + closing beats
          play on a clean stage). On mobile, stays hidden until the intro
          beats finish (Presenter-only opening). */}
      <AnimatePresence initial={false}>
      {!flowConfirmed && (!isMobile || introDone) && (
      <motion.div
        exit={{ opacity: 0, transition: { duration: 0.45 } }}
        className="absolute"
        style={{
          top: "48%",
          left: "6%",
          right: "24%",
          transform: "translateY(-50%)",
          zIndex: 30,
        }}
        data-testid="voice-sentence-bar"
      >
        <div
          className="px-8 py-6 md:px-12 md:py-7 shadow-[0_18px_45px_rgba(0,0,0,0.35)]"
          style={{
            background: "rgba(238, 218, 178, 0.86)",
            border: "1px solid rgba(90,60,25,0.14)",
          }}
        >
          <VocalScore
            emphasis={emphasis}
            pauses={pauses}
            interactive={interactive}
            onToggleWord={toggleWord}
            onTogglePause={togglePause}
          />
        </div>
      </motion.div>
      )}
      </AnimatePresence>

      {/* Presenter — right side. MODE A large during coaching, MODE B
          smaller during the exercise (background is clean, nothing to cover). */}
      {presenter && (
        <motion.div
          className="absolute overflow-hidden"
          animate={{
            right: isLeaving ? "-30%" : (modeCoaching ? "calc(-2% - 130px)" : "calc(1% - 100px)"),
            bottom: modeCoaching ? "-5.94%" : "-5%",
            height: modeCoaching ? "95.76%" : "80%",
            opacity: isLeaving ? 0 : 1,
          }}
          initial={{ right: "-30%", bottom: "-7.1%", height: "114%", opacity: 1 }}
          transition={isLeaving
            ? { duration: 0.85, ease: [0.4, 0, 0.2, 1] }
            : MODE_TRANSITION}
          style={{ zIndex: 20 }}
          data-testid="voice-presenter"
        >
          <PresenterFloorShadow />
          <AnimatePresence initial={false} mode="popLayout">
            <motion.img
              key={pose}
              src={presenterSrc}
              alt={presenter.name}
              draggable={false}
              initial={{ opacity: 0, y: 4, scaleX: flipHorizontally ? -1 : 1 }}
              animate={{ opacity: 1, y: 0, scaleX: flipHorizontally ? -1 : 1 }}
              exit={{ opacity: 0, y: -4, scaleX: flipHorizontally ? -1 : 1 }}
              transition={{ duration: 0.5, ease: EASE }}
              className="h-full w-auto select-none block"
              style={{
                mixBlendMode: "multiply",
                filter: "brightness(0.95) sepia(0.1)",
                WebkitMaskImage: PRESENTER_MASK,
                maskImage: PRESENTER_MASK,
              }}
              data-testid={`voice-presenter-img-${pose}`}
            />
          </AnimatePresence>
        </motion.div>
      )}

      {/* Speech bubble — only during coaching modes */}
      <AnimatePresence initial={false}>
        {showBubble && overrideMessage ? (
          <motion.div
            key={overrideMessage}
            initial={{ opacity: 0, y: 8, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.98 }}
            transition={{ duration: 0.35, ease: EASE }}
            className="absolute"
            style={{ top: 60, right: 170, zIndex: 100, maxWidth: 380 }}
            data-testid="voice-bubble"
          >
            <div
              className="relative bg-white text-[#1B1B18] px-7 py-5 shadow-[0_16px_40px_rgba(0,0,0,0.4)]"
              style={{ borderRadius: 30 }}
            >
              <p className="font-serif text-xl leading-snug">{overrideMessage}</p>
              {/* Tail — points down-right toward the presenter's mouth */}
              <span
                aria-hidden="true"
                className="absolute"
                style={{
                  right: 40,
                  bottom: -16,
                  width: 0,
                  height: 0,
                  borderLeft: "10px solid transparent",
                  borderRight: "16px solid transparent",
                  borderTop: "20px solid #ffffff",
                  filter: "drop-shadow(0 4px 4px rgba(0,0,0,0.15))",
                }}
              />
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>

      {/* Corner mark — top-right */}
      <div
        className="absolute right-6 top-5 flex flex-col items-end pointer-events-none"
        style={{ zIndex: 50 }}
        data-testid="voice-cornermark"
      >
        <p className="font-serif italic text-paper text-lg md:text-2xl leading-none tracking-wide drop-shadow-[0_2px_6px_rgba(0,0,0,0.55)]">
          InterAct Globally
        </p>
        <p className="font-hand text-mustard text-base md:text-xl leading-tight mt-1 drop-shadow-[0_2px_6px_rgba(0,0,0,0.55)]">
          the rehearsal room
        </p>
      </div>

      {/* Progress hint during the exercise */}
      {/* Progress hint — sits AT THE TOP OF the sentence bar so it stays
          readable against the warm cream background. */}
      {introDone && !flowConfirmed && (
        <p
          className="absolute font-mono uppercase tracking-[0.22em] text-[15px] font-bold text-terracotta"
          style={{
            top: "calc(48% - 92px)",
            left: "6%",
            zIndex: 45,
            background: "rgba(238, 218, 178, 0.86)",
            padding: "6px 14px",
          }}
          data-testid="voice-progress"
        >
          emphasis {emphasis.length}/{MAX_EMPHASIS} &nbsp;·&nbsp; pauses {pauses.length}/{MAX_PAUSES}
          {hasMinChoices && " · press continue when you\u2019re done"}
        </p>
      )}

      {/* Reflection cards — after the score is confirmed, before the closing beats.
          Refers ONLY to the emphasis + pause exercise. */}
      <AnimatePresence>
        {inReflectPick && (
          <motion.div
            key="voice-reflection-cards"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.5, ease: EASE }}
            className="absolute grid grid-cols-2 gap-3"
            style={{ top: "32%", left: "6%", right: "36%", zIndex: 60 }}
            data-testid="voice-reflection-cards"
          >
            {REFLECTION_OPTIONS.map((opt) => (
              <button
                key={opt.id}
                type="button"
                onClick={() => pickReflection(opt.id)}
                data-testid={`voice-reflection-${opt.id}`}
                className="text-left px-5 py-3 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_14px_36px_rgba(0,0,0,0.3)]"
                style={{
                  background: "rgba(242, 235, 218, 0.92)",
                  border: "1px solid rgba(90,60,25,0.16)",
                  boxShadow: "0 6px 18px rgba(0,0,0,0.2)",
                }}
              >
                <p className="font-serif text-lg md:text-xl leading-snug text-ink">{opt.label}</p>
                <p className="mt-0.5 font-body text-xs md:text-sm text-ink/70">&ldquo;{opt.hint}&rdquo;</p>
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Intro/success tap catcher */}
      {inCatcher && (
        <button
          type="button"
          onClick={advance}
          data-testid="voice-advance"
          aria-label="Continue"
          className="absolute inset-0 cursor-pointer bg-transparent pointer-events-auto"
          style={{ zIndex: 88 }}
        >
          <span
            aria-hidden="true"
            className="absolute bottom-24 left-1/2 -translate-x-1/2 font-mono text-[10px] uppercase tracking-[0.28em] text-paper/70 animate-pulse"
          >
            tap to continue &nbsp;·&nbsp; {currentCatcherStep} / {totalCatcherSteps}
          </span>
        </button>
      )}
    </div>
  );
}

export default RehearsalRoomVoice;
